import {
  Button,
  Card,
  CardActionArea,
  CardActionAreaProps,
  Chip,
  ChipProps,
  Tooltip,
  TooltipProps,
  Typography,
  tooltipClasses,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { useTimeSinceNow } from '../../../Utilities/useTimeSinceNow'
import { ThreeDIcon } from '../../Icons'
import { HollowHeartIcon, BusIcon, GoodFitIcon } from '../../Icons/Icons'
import { Link as RouterLink } from 'react-router-dom'
import { QuickDeclineIcon } from './Icons'

export const CardListItem = styled('li', {
  shouldForwardProp: (propName) => propName !== 'size',
})<{ size?: 'large' | 'small' }>(({ theme, size }) => ({
  [theme.breakpoints.up('sm')]: {
    maxWidth:
      size === 'small' ? 'calc((100% - 2rem) / 3)' : 'calc((100% - 1rem) / 2)',
  },
  [theme.breakpoints.up('lg')]: {
    maxWidth:
      size === 'small' ? 'calc((100% - 3rem) / 4)' : 'calc((100% - 2rem) / 3)',
  },
  width: '100%',
  flex: '1 0 100%',
}))

export const HBCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'transparent',
  border: 'none',
  boxShadow: 'none',
  overflow: 'inherit',
  '&:hover': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
  '& .Mui-focusVisible': {
    '& .main-card-button': {
      border: '3px solid rgba(19, 112, 184, 0.85)',
      borderRadius: '4px',
      height: '40px',
    },
  },
  '&.declined-venue-card': {
    img: {
      filter: 'contrast(0.4) brightness(1.6)',
    },
    button: {
      backgroundColor: 'white',
      border: `1px solid #595959`,
      color: '#595959',
    },
  },
}))

export const CardImageContainer = styled('div')({
  position: 'relative',
})

export const DeclineCardImageContainer = styled(CardImageContainer)({
  backgroundColor: '#F5F5F5',
  borderRadius: '8px',
})
export const ImageOverlay = styled('div')({
  position: 'absolute',
  top: 0,
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
})

const QuckDeclineButton = styled(Button)<{ isTouchScreen: boolean }>(
  ({ theme, isTouchScreen }) => ({
    height: 40,
    width: 40,
    borderRadius: '50%',
    position: 'absolute',
    right: 0,
    transform: 'translate(25px, -13px)',
    zIndex: 3,
    visibility: isTouchScreen ? 'visible' : 'hidden',
    opacity: isTouchScreen ? 1 : 0,
    transition: 'visibility 0.2s, opacity 0.2s',
    '&:hover': {
      background: 'none',
    },
    '&:focus, &:focus-visible': {
      visibility: 'visible',
      opacity: 1,
      border: '3px solid rgba(19, 112, 184, 0.85)',
      borderRadius: '4px',
      height: '40px',
    },
  })
)

const DeclineTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip
    {...props}
    arrow
    classes={{ popper: className }}
    PopperProps={{
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, -14],
          },
        },
      ],
    }}
  />
))(({ theme }) => ({
  opacity: 0.85,
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
  },
}))

interface HBCardActionAreaProps extends Omit<CardActionAreaProps<'a'>, 'sx'> {
  href: string
}
export const HBCardActionArea = ({
  href,
  ...props
}: HBCardActionAreaProps): JSX.Element => {
  const styles = {
    backgroundColor: 'transparent',
    border: 'none',
    boxShadow: 'none',
    '& .MuiCardActionArea-focusHighlight': {
      color: 'white',
    },
    '&:hover  .MuiCardActionArea-focusHighlight': {
      opacity: 0.2,
    },
    '&:hover, &:focus, &:focus-visible': {
      '& .decline': {
        visibility: 'visible',
        opacity: 1,
        transition: 'visibility 0.2s, opacity 0.2s',
      },
    },
  }
  return (
    <CardActionArea component={RouterLink} to={href} {...props} sx={styles} />
  )
}
export const VenueInformationContainer = styled('div')({
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  gap: '16px',
  minHeight: '34px',
  '& > *': {
    minWidth: 0,
  },
})

interface MatchedAtChipProps {
  matchedDate?: string
}

export const MatchedAtChip = ({
  matchedDate,
}: MatchedAtChipProps): JSX.Element => (
  <Chip
    variant='subtle'
    size='small'
    style={{ flexShrink: 0 }}
    icon={<HollowHeartIcon titleAccess='Matched' />}
    label={useTimeSinceNow(matchedDate).timeSince}
  />
)

export const LocationChip = ({
  location,
  size = 'small',
  ...rest
}: { location: string; size?: 'small' | 'medium' } & Omit<
  ChipProps,
  'size'
>): JSX.Element => (
  <Chip
    variant='subtle'
    size={size}
    color='info'
    icon={<BusIcon titleAccess='Nearest station' />}
    label={location}
    {...rest}
  />
)

export const ThreeDTourChip = (): JSX.Element => (
  <Chip
    icon={<ThreeDIcon />}
    label='3D Tour'
    variant='filled'
    color='primary'
    size='small'
    sx={{ margin: '0.5rem', '& .MuiChip-icon': { fontSize: '1em' } }}
  />
)

export const QuickDeclineButton = ({
  handleQuickDecline,
  isTouchScreen,
}: {
  handleQuickDecline: () => void
  isTouchScreen: boolean
}): JSX.Element => (
  <DeclineTooltip
    PopperProps={{ keepMounted: true }}
    placement='left'
    title='Decline this venue'
  >
    <QuckDeclineButton
      tabIndex={0}
      isTouchScreen={isTouchScreen}
      aria-hidden='true'
      onClick={(e) => {
        e.preventDefault()
        handleQuickDecline()
      }}
      className='decline'
    >
      <Typography variant='visuallyHidden'>Decline the venue</Typography>
      <QuickDeclineIcon />
    </QuckDeclineButton>
  </DeclineTooltip>
)

export const GoodFitChip = ({
  size = 'small',
  ...rest
}: { size?: 'small' | 'medium' } & Omit<ChipProps, 'size'>): JSX.Element => (
  <Chip
    variant='subtle'
    size={size}
    color='info'
    icon={<GoodFitIcon />}
    label='Great fit'
    {...rest}
  />
)
